import React, { useEffect, useState } from 'react';
import { BirdzNotif, BirdzTitle, useNotif } from '@applications-terrains/birdz-react-library';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import SelectWithSearchModal from '../../../Tools/Selects/SelectWithSearchModal';
import axios, { AxiosError } from 'axios';
import { Form, Formik, FormikValues } from 'formik';
import { useGetCompanies } from '../../../../hooks/datarefs';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { useTranslation } from 'react-i18next';

export default function FP2ESensorBrandForm({
  endpoint,
  setRequestStatus,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: number | null;
}) {
  const { t } = useTranslation();
  const { notif, notifOptions } = useNotif();
  const [values, setValues] = useState<FormikValues>();
  const [isError, setIsError] = useState<boolean>();
  const initialValues = {
    Code: null,
    Label: null,
    Models: [],
    companies: []
  };

  const { data: clients } = useGetCompanies();
  const [error, setError] = useState<Record<string, string>>();

  useEffect(() => {
    if (action === 'edit' && id !== null) {
      axios.get(endpoint + `${id}/`).then((response) => {
        setValues(response.data);
      });
    }
  }, [action, id]);

  const handleSubmit = async (values: FormikValues) => {
    const payload = values;

    if (!id) {
      await axios
        .post(endpoint, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error) => {
          const result = error.response.data as Record<string, string>;
          setError(result);
          {
            !result &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    } else {
      await axios
        .put(endpoint + `${id}`, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error: AxiosError<Record<string, string>>) => {
          const result = error.response?.data;
          setError(result);
          {
            !result &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    }
  };

  return (
    <>
      <BirdzTitle>{t('referencesTables.fp2e.sensorBrand')}</BirdzTitle>
      <Formik
        initialValues={values ? values : initialValues}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          const needRequiredFields = !values.Code || !values.Label;
          setIsError(needRequiredFields);
          return (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type={'text'}
                      name={'Code'}
                      value={values.Code || ''}
                      fullWidth
                      onChange={(e) => {
                        setFieldValue('Code', e.target.value);
                      }}
                      label={t('referencesTables.codeIndication') + '*'}
                      InputLabelProps={{ shrink: true }}
                    />
                    {values.Code && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('Code', null)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {error && 'Code' in error && (
                    <Alert sx={{ mt: 1 }} severity="error">
                      {error['Code']}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type={'text'}
                      name={'Label'}
                      value={values.Label || ''}
                      fullWidth
                      onChange={(e) => {
                        setFieldValue('Label', e.target.value);
                      }}
                      label={t('referencesTables.brand') + '*'}
                      InputLabelProps={{ shrink: true }}
                    />
                    {values.Label && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('Label', null)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <fieldset
                    style={{
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: 4,
                      padding: '0 8px 8px 8px',
                      margin: 0
                    }}
                  >
                    <legend
                      style={{
                        fontSize: '0.75rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        padding: '0 4px',
                        lineHeight: 1
                      }}
                    >
                      {t('referencesTables.models')}
                    </legend>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('referencesTables.code')}</TableCell>
                          <TableCell>{t('referencesTables.name')}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(values.Models ?? []).map(
                          (model: { Code: string; Label: string }, index: number) => (
                            <TableRow key={index}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={model.Code || ''}
                                  onChange={(e) => {
                                    const updated = [...values.Models];
                                    updated[index] = { ...updated[index], Code: e.target.value };
                                    setFieldValue('Models', updated);
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={model.Label || ''}
                                  onChange={(e) => {
                                    const updated = [...values.Models];
                                    updated[index] = { ...updated[index], Label: e.target.value };
                                    setFieldValue('Models', updated);
                                  }}
                                />
                              </TableCell>
                              <TableCell width={40}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updated = values.Models.filter(
                                      (_: any, i: number) => i !== index
                                    );
                                    setFieldValue('Models', updated);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setFieldValue('Models', [
                                  ...(values.Models ?? []),
                                  { Code: '', Label: '' }
                                ]);
                              }}
                            >
                              {t('referencesTables.addLine')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </fieldset>
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    multiple
                    label={t('referencesTables.clients')}
                    options={(clients ?? []).map((c) => ({ label: c.Name, value: c.Id }))}
                    value={values.companies ?? []}
                    onChange={(val) => setFieldValue('companies', val)}
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setRequestStatus(null);
                      setAction(null);
                    }}
                  >
                    {t('referencesTables.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={isError}>
                    {action === 'edit' ? t('referencesTables.edit') : t('referencesTables.add')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <BirdzNotif options={notifOptions} />
    </>
  );
}
